import React from "react";
import NumberFormat from "react-number-format";
import Badge from "Utils/Badge/Badge";
import { RenderKgSecos } from "../../components/CeldasReporte";
import {
    BADGE_POR_TIPO_DIFERENCIA,
    CAMPOS_DIFERENCIA_ACOPIO,
    TEXTO_PERIODO_SIN_ENVIOS,
    TIPO_DIFERENCIA_MANUAL,
    TIPO_SIN_ENVIOS,
} from "../helpers/tiposDiferenciaAcopio";

const esVacio = (valor) => valor === null || valor === undefined;

// Sin traslados en los meses del periodo no hay con qué calcular la diferencia
const KgSecosDeTraslados = ({ value }) =>
    esVacio(value) ? (
        <span className="text-muted">Sin traslados</span>
    ) : (
        <RenderKgSecos value={value} />
    );

// Con edición, las diferencias de los periodos compartidos se escriben aquí;
// sin ella se muestran, o se marcan como pendientes si todavía no se guardaron
const CeldaDiferencia = ({ periodo, campo, edicion }) => {
    const esManual = periodo.tipo_diferencia_acopio === TIPO_DIFERENCIA_MANUAL;

    if (esManual && edicion) {
        const valor = (edicion.valores[periodo.periodo_id] || {})[campo];

        return (
            <div className="ml-auto" style={{ maxWidth: "150px" }}>
                <NumberFormat
                    className="form-control text-right"
                    value={esVacio(valor) ? "" : valor}
                    thousandSeparator=","
                    decimalScale={2}
                    placeholder="0.00"
                    onValueChange={(values) =>
                        edicion.cambiarValor(
                            periodo.periodo_id,
                            campo,
                            values.floatValue,
                        )
                    }
                />
            </div>
        );
    }

    if (esManual && esVacio(periodo[campo])) {
        return <Badge text="Pendiente" variant="warning" />;
    }

    return <KgSecosDeTraslados value={periodo[campo]} />;
};

// Lo que se le registró a cada padre PT que compartió el periodo, o pendiente
// si ese periodo todavía no entró en uno de sus cierres
const OtrosPadres = ({ otros }) =>
    otros.map((otro) => (
        <div
            key={otro.padre_producto_terminado_id}
            className="d-flex align-items-center justify-content-center text-nowrap"
        >
            <strong className="mr-2">{otro.nombre}:</strong>
            {otro.pendiente ? (
                <Badge text="Pendiente" variant="warning" />
            ) : (
                <div className="text-left">
                    <div>
                        Lab:{" "}
                        <RenderKgSecos value={otro.diferencia_acopio_lab} />
                    </div>
                    <div>
                        Liq:{" "}
                        <RenderKgSecos
                            value={otro.diferencia_acopio_liquidacion}
                        />
                    </div>
                </div>
            )}
        </div>
    ));

// Diferencia acopio de cada periodo del cierre, con la de los traslados de sus
// meses como referencia. La edición es opcional: sin ella la tabla solo muestra.
// La referencia sirve para repartir la diferencia antes de cerrar, así que un
// cierre ya registrado la oculta
export const DiferenciasAcopioTable = ({
    periodos,
    edicion,
    mostrarReferencia = true,
}) => (
    <div className="new-table-wrapper mt-4 mb-3">
        <table>
            <thead>
                <tr>
                    <th rowSpan={2} className="align-middle">
                        Periodo
                    </th>
                    <th rowSpan={2} className="align-middle">
                        Tipo
                    </th>
                    {mostrarReferencia && (
                        <th
                            colSpan={2}
                            className="text-center cell-border-left"
                            title="Diferencia de los traslados de los meses del periodo, de todos los productos padre"
                        >
                            Referencia de traslados
                        </th>
                    )}
                    <th colSpan={2} className="text-center cell-border-left">
                        Diferencia acopio
                    </th>
                    <th rowSpan={2} className="align-middle cell-border-left">
                        Otros productos padre
                    </th>
                </tr>
                <tr>
                    {mostrarReferencia && (
                        <React.Fragment>
                            <th className="text-right cell-border-left">
                                Laboratorio
                            </th>
                            <th className="text-right">Liquidación</th>
                        </React.Fragment>
                    )}
                    <th className="text-right cell-border-left">Laboratorio</th>
                    <th className="text-right">Liquidación</th>
                </tr>
            </thead>

            <tbody>
                {periodos.map((periodo) => (
                    <tr key={periodo.periodo_id}>
                        <td className="text-nowrap text-center">
                            {periodo.periodo}
                        </td>

                        <td className="text-center">
                            <Badge
                                {...BADGE_POR_TIPO_DIFERENCIA[
                                    periodo.tipo_diferencia_acopio
                                ]}
                            />
                        </td>

                        {periodo.tipo_diferencia_acopio === TIPO_SIN_ENVIOS ? (
                            <td
                                colSpan={mostrarReferencia ? 5 : 3}
                                className="text-muted cell-border-left text-center cell-border-top cell-border-bottom"
                            >
                                {TEXTO_PERIODO_SIN_ENVIOS}
                            </td>
                        ) : (
                            <React.Fragment>
                                {mostrarReferencia && (
                                    <React.Fragment>
                                        <td className="text-right text-nowrap cell-border-left">
                                            <KgSecosDeTraslados
                                                value={periodo.referencia_lab}
                                            />
                                        </td>
                                        <td className="text-right text-nowrap">
                                            <KgSecosDeTraslados
                                                value={
                                                    periodo.referencia_liquidacion
                                                }
                                            />
                                        </td>
                                    </React.Fragment>
                                )}

                                {CAMPOS_DIFERENCIA_ACOPIO.map(
                                    (campo, indice) => (
                                        <td
                                            key={campo}
                                            className={`text-right text-nowrap ${
                                                indice === 0
                                                    ? "cell-border-left"
                                                    : ""
                                            }`}
                                        >
                                            <CeldaDiferencia
                                                periodo={periodo}
                                                campo={campo}
                                                edicion={edicion}
                                            />
                                        </td>
                                    ),
                                )}

                                <td className="cell-border-left text-center">
                                    <div className="d-flex flex-column gap-10">
                                        {periodo.otros_padres &&
                                        periodo.otros_padres.length > 0 ? (
                                            <OtrosPadres
                                                otros={periodo.otros_padres}
                                            />
                                        ) : (
                                            "—"
                                        )}
                                    </div>
                                </td>
                            </React.Fragment>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);
